import React, { useState, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import clsx from 'clsx';
import { useUpdateLead } from 'src/services/api';
import { useOpenTourSuggestion } from 'src/services/api/messages/use-tour-slot-suggestions';

import { ThemeProvider } from 'src/theme';
import { FlexColumn } from 'src/components/flex';
import PageTitle from 'src/components/page-title/PageTitle';
import { JustAddedFactsProvider } from 'src/components/lead-sidebar/just-added-facts-context';
import Provider from 'src/pages/landlord/leads/messages/context/Provider';
import { useLeadMessagesMode } from 'src/pages/landlord/leads/messages/context/mode';
import ComposerProvider from 'src/pages/landlord/leads/messages/context/ComposerProvider';
import { useComposer } from 'src/pages/landlord/leads/messages/context/composer-hooks';
import { useLeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/hooks';
import MessagesContainer from 'src/pages/landlord/leads/messages/components/messages-container';
import Header from 'src/pages/landlord/leads/messages/components/header';
import Input from 'src/pages/landlord/leads/messages/components/input';
import Hotkeys from 'src/pages/landlord/leads/messages/components/hotkeys/LeadMessagesHotkeys';
import LeadSidebar from 'src/components/lead-sidebar/LeadSidebar';
import LeadFactsModal from 'src/pages/landlord/leads/messages/components/lead-facts-modal';
import SentryErrorBoundary from 'src/components/sentry-error-boundary';
import SnippetsModal from 'src/pages/landlord/leads/messages/components/snippets-modal';
import CreateOrUpdateSnippetModal from 'src/pages/landlord/snippets/components/create-or-update-snippet-modal';
import Nudges from 'src/pages/landlord/leads/messages/components/nudges';
import { SimilarPropertiesProvider } from 'src/components/similar-properties-modal/SimilarPropertiesContext';
import CannotReplyNotice from 'src/pages/landlord/leads/messages/components/notice-bars/CannotReplyNotice';
import OwnerAwayNotice from 'src/pages/landlord/leads/messages/components/notice-bars/OwnerAwayNotice';
import analyticsClient, { AnalyticsEvent } from 'src/services/analytics';
import { useLandlordLayoutContext } from 'src/components/layout/landlord/LandlordLayoutProvider';
import { usePersistentState } from 'src/services/storage/use-persistent-state';

import styles from 'src/pages/landlord/leads/messages/LeadMessagesPage.module.css';
import CreateOrUpdateTourModal from 'src/components/create-or-update-tour-modal';

type RegisterComposerInsertProps = {
    insertRef: React.MutableRefObject<(text: string) => void>;
};

/**
 * Hands the composer's `insertText` to the parts of the page rendered outside
 * ComposerProvider (the sidebar), via a ref, so e.g. "add to message" actions can
 * append to the draft without widening the provider's remount boundary.
 */
function RegisterComposerInsert({ insertRef }: RegisterComposerInsertProps) {
    const { insertText } = useComposer();

    useEffect(() => {
        insertRef.current = insertText;
    }, [insertRef, insertText]);

    return null;
}

function LeadMessagesPageContent() {
    const {
        showings,
        lead,
        property,
        messages,
        attachments,
        snippets,
        stages,
        interests,
        teamMembers,
        isSnippetsModalOpen,
        setIsSnippetsModalOpen,
        navigateToPreviousConversation,
        navigateToNextConversation,
        loggedInUser,
        isOwnerAway,
        isAvailabilityEnabled,
        isTourTimeSuggestionsEnabled,
    } = useLeadMessagesPageContext();

    const isOwnLead = loggedInUser.id === lead.owner;
    const { data: openTourSuggestion } = useOpenTourSuggestion(
        lead.uuid,
        isTourTimeSuggestionsEnabled && isOwnLead
    );
    // Three mutually-exclusive states for a lead the user does not own:
    // 1. availability feature off  -> cannot reply, show the plain warning
    // 2. feature on, owner online  -> cannot reply, show the plain warning
    // 3. feature on, owner away    -> may cover the shift, show the "away" bar
    const isTeammateLead = !isOwnLead;
    const isOwnerAwayTeammate = isTeammateLead && isAvailabilityEnabled && isOwnerAway;
    const isOwnerOnlineTeammate = isTeammateLead && isAvailabilityEnabled && !isOwnerAway;
    const isTeammateLeadFeatureOff = isTeammateLead && !isAvailabilityEnabled;
    // The plain "does not belong to you" warning covers both no-reply cases.
    const showCannotReplyWarning = isTeammateLeadFeatureOff || isOwnerOnlineTeammate;
    const { mutate: updateLead, isLoading: isAssigningLead } = useUpdateLead(lead.uuid);
    const assignLeadToSelf = () => {
        updateLead({
            uuid: lead.uuid,
            name: lead.name,
            email: lead.email,
            phone_number: lead.phone_number,
            property_id: lead.property_id,
            owner: loggedInUser.id,
        });
    };

    const { isDrawerOpen } = useLandlordLayoutContext();
    const { variant } = useLeadMessagesMode();

    const [isSidebarCollapsed, setIsSidebarCollapsed] = usePersistentState(
        'lead-sidebar-collapsed',
        false,
        (value): value is boolean => typeof value === 'boolean'
    );
    const toggleSidebarCollapsed = () => setIsSidebarCollapsed(!isSidebarCollapsed);

    const [isShowLeadDetailsModal, setIsShowLeadDetailsModal] = useState(false);
    const [isAddingSnippet, setIsAddingSnippet] = useState(false);
    const [isTourModalOpen, setIsTourModalOpen] = useState(false);

    const openTourModal = () => {
        setIsTourModalOpen(true);
    };

    // The sidebar lives outside ComposerProvider; RegisterComposerInsert (mounted
    // inside it) keeps this pointing at the current composer's insertText.
    const insertIntoComposerRef = useRef<(text: string) => void>(() => {});

    const handleCloseShowingModal = () => {
        setIsTourModalOpen(false);
    };

    useEffect(() => {
        const handleKeyDown = debounce((event: KeyboardEvent) => {
            const isUserTyping = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName as string) ||
                (document.activeElement as HTMLElement)?.isContentEditable;
            if (isUserTyping) {
                return;
            }
            const isSidebarToggleKey = variant === 'drawer' && ['ArrowLeft', 'ArrowRight'].includes(event.key);

            if (['ArrowUp', 'ArrowDown', 'T', 't'].includes(event.key) || isSidebarToggleKey) {
                analyticsClient.track(AnalyticsEvent.SHORTCUT_KEY_USED, {
                    page: 'Lead messages',
                    key: event.key,
                });
            }

            switch (event.key) {
                case 'ArrowUp':
                    navigateToPreviousConversation();
                    break;
                case 'ArrowDown':
                    navigateToNextConversation();
                    break;
                case 'ArrowLeft':
                    if (variant === 'drawer') {
                        setIsSidebarCollapsed(false);
                    }
                    break;
                case 'ArrowRight':
                    if (variant === 'drawer') {
                        setIsSidebarCollapsed(true);
                    }
                    break;
                case 't':
                case 'T':
                    setIsTourModalOpen(true);
                    break;
                case 's':
                case 'S':
                    setIsSnippetsModalOpen(true);
                    break;
            }

            if (['ArrowUp', 'ArrowDown', 'T', 't', 'S', 's'].includes(event.key) || isSidebarToggleKey) {
                event.preventDefault();
                event.stopPropagation();
            }
        }, 100);

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [navigateToPreviousConversation, navigateToNextConversation, variant, setIsSidebarCollapsed]);


    return (
        <SimilarPropertiesProvider
            lead={lead}
            property={property}
            onInsertMessage={(text) => insertIntoComposerRef.current(text)}
        >
            <div className={clsx(styles.root, isDrawerOpen && styles.drawerOpen, variant === 'drawer' && styles.drawerVariant)}>
                <FlexColumn flex={1} rowGap={0} className={styles.messagesArea}>
                    <Header
                        handleShowLeadDetails={setIsShowLeadDetailsModal}
                        openTourModal={() => openTourModal()}
                        isSidebarCollapsed={isSidebarCollapsed}
                        onToggleSidebarCollapsed={toggleSidebarCollapsed}
                    />
                    {isShowLeadDetailsModal && (
                        <LeadFactsModal
                            open={isShowLeadDetailsModal}
                            onClose={() => setIsShowLeadDetailsModal(false)}
                            lead={lead}
                            property={property}
                            showings={showings}
                        />)}

                    <MessagesContainer />
                    {showCannotReplyWarning && (
                        <CannotReplyNotice
                            isAssigningLead={isAssigningLead}
                            onAssignToSelf={assignLeadToSelf}
                        />
                    )}
                    {isOwnerAwayTeammate && <OwnerAwayNotice />}
                    <ComposerProvider key={lead.uuid}>
                        <RegisterComposerInsert insertRef={insertIntoComposerRef} />
                        <FlexColumn rowGap={0}>
                            <SentryErrorBoundary>
                                {!showCannotReplyWarning && <Nudges openTourModal={openTourModal} />}
                                <Input />
                            </SentryErrorBoundary>
                            <Hotkeys />
                        </FlexColumn>

                        <SnippetsModal
                            snippets={snippets}
                            open={isSnippetsModalOpen}
                            onClose={setIsSnippetsModalOpen}
                            setIsAddingSnippet={setIsAddingSnippet}
                            setSnippetsModalOpen={setIsSnippetsModalOpen}
                        />
                    </ComposerProvider>
                </FlexColumn>

                <div
                    className={clsx(
                        styles.sidebarWrapper,
                        variant === 'drawer' && isSidebarCollapsed && styles.sidebarCollapsed
                    )}
                >
                    <LeadSidebar
                        lead={lead}
                        property={property}
                        openTourSuggestion={openTourSuggestion}
                        showings={showings}
                        messages={messages}
                        insertIntoReplyInput={(text) => insertIntoComposerRef.current(text)}
                        attachments={attachments}
                        stages={stages}
                        interests={interests}
                        teamMembers={teamMembers}
                        variant={variant === 'drawer' ? 'drawer' : undefined}
                    />
                </div>

                {isAddingSnippet &&
                    <CreateOrUpdateSnippetModal onClose={() => setIsAddingSnippet(false)} />
                }
                {isTourModalOpen && (
                    <CreateOrUpdateTourModal
                        open
                        onClose={handleCloseShowingModal}
                        selectedLeadUuid={lead.uuid}
                        landlordUserId={loggedInUser.id}
                        leads={[lead]}
                        initPropertyId={lead.property_id}
                        onCreated={handleCloseShowingModal}
                    />)}
            </div>
        </SimilarPropertiesProvider>
    );
}

export default function LeadMessagesSurface() {
    const { variant } = useLeadMessagesMode();

    return (
        <ThemeProvider>
            <Provider>
                {variant === 'page' ? (
                    <PageTitle title="Reffie | Messages">
                        <JustAddedFactsProvider>
                            <LeadMessagesPageContent />
                        </JustAddedFactsProvider>
                    </PageTitle>
                ) : (
                    <JustAddedFactsProvider>
                        <LeadMessagesPageContent />
                    </JustAddedFactsProvider>
                )}
            </Provider>
        </ThemeProvider>
    );
}
